// src/components/ui/OutcomeDialog.jsx
//
// The modal that reports what an action did and waits for «OK» (v202, first used by the Gestione Workspace
// console in place of its outcome banner). `outcome` = `{ tone, text, action? }`; null renders nothing.
//
// ⛔ `text` is a sentence the CALLER wrote — never a server `message`/`warning` nor `err.message`.
// ⭐ v204: `<b>…</b>` in that sentence (our copy only) renders as <strong>, built as React elements — never parsed as
// HTML, so any other markup stays literal text (ui/emphasis.jsx). `text` stays a string, so the panel's key still tracks it.
// `tone` (info / success / warning) picks the icon and palette and nothing else; an unknown tone is info.
//
// «OK» is the default: focused on open, and Escape and a mousedown on the overlay mean OK too.
// `action` = an optional `{ label, onClick }` the outcome itself suggests («Reinvia invito», «Accedi di nuovo» —
// the latter SIGNS OUT): a secondary button before OK, never focused first, never reached by Escape or the
// overlay. Clicking it closes the dialog and runs it in the same handler, so a dialog it opens takes over.
//
// ⛔ Not AdminModal: that one does not portal, manages no focus and hard-codes its title id. Shared with it:
// modalStack.js — Escape and the Tab trap act only while this is the top-most modal. Portalled to document.body,
// so it paints above the in-place dialogs (same z-index, later in the DOM). Focus goes back on close to whatever
// had it, if that element is still on the page.
import React, { useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, CheckCircle2, Info } from 'lucide-react';
import { COLORS, FONT_SANS, RADII, SEMANTIC } from '../../constants/theme.js';
import { useModalLayer } from './modalStack.js';
import { withEmphasis } from './emphasis.jsx';
import { useContentInset } from './modalArea.js';

const TONES = {
  info: { palette: SEMANTIC.info, Icon: Info },
  success: { palette: SEMANTIC.verde, Icon: CheckCircle2 },
  warning: { palette: SEMANTIC.attenzione, Icon: AlertTriangle },
};

const FOCUSABLE = 'button:not([disabled])';

// Same overlay tint and panel shadow as AdminModal / WizardModal (kept literal there too).
const OVERLAY = {
  position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.4)',
  display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px', fontFamily: FONT_SANS,
};
const PANEL = {
  background: COLORS.surface, borderRadius: RADII.control, width: '100%', maxWidth: '460px',
  maxHeight: 'calc(100vh - 32px)', overflowY: 'auto', padding: '24px',
  boxShadow: '0 8px 40px rgba(0,0,0,0.15)', outline: 'none',
};
const ROW = { display: 'flex', alignItems: 'flex-start', gap: '14px' };
const tile = (palette) => ({
  width: '40px', height: '40px', borderRadius: RADII.pill, flexShrink: 0,
  background: palette.bg, color: palette.fg,
  display: 'flex', alignItems: 'center', justifyContent: 'center',
});
const TEXT = { margin: 0, paddingTop: '9px', fontSize: '14px', lineHeight: '22px', color: COLORS.ink };
const BUTTONS = { display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '24px' };
const BTN = {
  height: '40px', padding: '0 18px', borderRadius: RADII.controlInner, fontSize: '13px',
  fontFamily: FONT_SANS, cursor: 'pointer',
};
const SECONDARY = { ...BTN, border: `1px solid ${COLORS.borderStrong}`, background: COLORS.surface, color: COLORS.ink };
const PRIMARY = { ...BTN, border: 'none', background: COLORS.accent, color: COLORS.surface, fontWeight: 600 };

export default function OutcomeDialog({ outcome, onClose }) {
  const { t } = useTranslation('common');
  const open = Boolean(outcome);
  const textId = useId();
  const isTop = useModalLayer(open);
  const insetLeft = useContentInset(open);
  const panelRef = useRef(null);
  const okRef = useRef(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  // What had focus before the dialog opened gets it back on close (if still on the page) …
  useEffect(() => {
    if (!open) return undefined;
    const previous = document.activeElement;
    return () => {
      if (previous && previous.isConnected && typeof previous.focus === 'function') previous.focus();
    };
  }, [open]);

  // … and every outcome — including one that REPLACES another while open (an action whose result is a new
  // outcome) — focuses OK. The panel is keyed by the outcome, so a replacement is a new alertdialog that
  // assistive technology announces again.
  useEffect(() => {
    if (outcome && okRef.current) okRef.current.focus();
  }, [outcome]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (!isTop()) return;
      if (e.key === 'Escape') {
        e.preventDefault();
        closeRef.current();
        return;
      }
      if (e.key !== 'Tab' || !panelRef.current) return;
      const items = Array.from(panelRef.current.querySelectorAll(FOCUSABLE));
      if (items.length === 0) return;
      const first = items[0];
      const last = items[items.length - 1];
      const active = document.activeElement;
      const inside = panelRef.current.contains(active);
      if (e.shiftKey && (!inside || active === first)) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && (!inside || active === last)) { e.preventDefault(); first.focus(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, isTop]);

  if (!open) return null;
  const toneKey = TONES[outcome.tone] ? outcome.tone : 'info';
  const { palette, Icon } = TONES[toneKey];
  const { action } = outcome;

  const runAction = () => {
    closeRef.current();
    action.onClick();
  };

  return createPortal(
    <div
      style={insetLeft ? { ...OVERLAY, paddingLeft: `${insetLeft + 16}px` } : OVERLAY}
      data-testid="outcome-dialog-overlay"
      onMouseDown={(e) => { if (e.target === e.currentTarget) closeRef.current(); }}
    >
      <div
        key={`${toneKey}:${outcome.text}`}
        ref={panelRef}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby={textId}
        data-tone={toneKey}
        tabIndex={-1}
        style={PANEL}
      >
        <div style={ROW}>
          <div style={tile(palette)} aria-hidden="true"><Icon size={20} /></div>
          <p id={textId} style={TEXT}>{withEmphasis(outcome.text)}</p>
        </div>
        <div style={BUTTONS}>
          {action && (
            <button type="button" onClick={runAction} style={SECONDARY}>{action.label}</button>
          )}
          <button type="button" ref={okRef} onClick={() => closeRef.current()} style={PRIMARY}>
            {t('actions.ok')}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
